import type { Document, DashboardStats, ReviewDecision } from '../types';

const API_BASE = '/api';

async function fetchApi(endpoint: string, options?: RequestInit) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.statusText}`);
  }

  return response.json();
}

export const documentsApi = {
  // Get dashboard statistics
  getDashboardStats: async (): Promise<DashboardStats> => {
    return fetchApi('/dashboard/stats');
  },

  // List documents with filters
  listDocuments: async (params?: {
    status?: string;
    documentType?: string;
    requiresReview?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{ documents: Document[]; total: number }> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    return fetchApi(`/documents?${queryParams.toString()}`);
  },

  // Get single document
  getDocument: async (documentId: string): Promise<Document> => {
    return fetchApi(`/documents/${documentId}`);
  },

  // Submit review decision
  submitReview: async (decision: ReviewDecision): Promise<void> => {
    return fetchApi(`/documents/${decision.documentId}/review`, {
      method: 'POST',
      body: JSON.stringify(decision),
    });
  },

  // Update document fields
  updateDocument: async (
    documentId: string,
    updates: Record<string, any>
  ): Promise<Document> => {
    return fetchApi(`/documents/${documentId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  // Trigger reprocessing
  reprocessDocument: async (documentId: string): Promise<void> => {
    return fetchApi(`/documents/${documentId}/reprocess`, {
      method: 'POST',
    });
  },

  // Export documents
  exportDocuments: async (params: {
    startDate: string;
    endDate: string;
    format: 'csv' | 'json' | 'accounting';
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_BASE}/export?${queryParams.toString()}`);
    
    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }
    
    return response.blob();
  },
};